import React, { useState, useMemo } from "react";
import createPlotlyComponent from "react-plotly.js/factory";
import Plotly from "plotly.js-dist-min";
import BasePlot from "./BasePlot";

const Plot = createPlotlyComponent(Plotly);

export interface ScatterPlotExpressionProps {
  data: any[];
  xMetric?: string;
  yMetric?: string;
  colorMetric?: string;
  groupKey?: string; // For cluster plots
  title?: string;
  defaultSize?: number;
  className?: string;
  plotRef?: string;
  showSizeSlider?: boolean;
  showLegend?: boolean;
  groupLabels?: Record<string, string>;
}

const ScatterPlotExpression = ({
  data,
  xMetric = "UMAP1",
  yMetric = "UMAP2",
  colorMetric = "expression",
  groupKey,
  title = "Scatter Plot (Expression)",
  defaultSize = 8,
  className = "",
  plotRef,
  showSizeSlider = true,
  showLegend = false,
  groupLabels = {},
}: ScatterPlotExpressionProps): React.ReactElement => {
  const [markerSize, setMarkerSize] = useState(defaultSize);

  
  




  // Memoize data processing to prevent unnecessary recalculations
  const plotData = useMemo(() => {
    
    
    // Determine which column to use for coloring
    let effectiveColorMetric = colorMetric;
    if (data.length > 0 && typeof data[0][colorMetric] === 'undefined') {
      // Fallback to 'expr' or any numeric column if colorMetric not found
      effectiveColorMetric = 'expr';
   
    }

    if (groupKey) {
      // Multi-trace for clusters
      const groupValue = (row: any) => {
        const value = row[groupKey];
        if (groupKey !== "annotation") return value;
        const label = String(value ?? "").trim().replace(/\s+/g, " ");
        return label ? label.charAt(0).toUpperCase() + label.slice(1) : label;
      };
      const uniqueGroups = [...new Set(data.map(groupValue))].sort(
        (a, b) => {
          const numA = parseFloat(a);
          const numB = parseFloat(b);
          if (!isNaN(numA) && !isNaN(numB)) {
            return numA - numB;
          }
          return String(a).localeCompare(String(b));
        },
      );

      const plotlyColors = [
        "#1f77b4", "#ff7f0e", "#2ca02c", "#d62728", "#9467bd",
        "#8c564b", "#e377c2", "#7f7f7f", "#bcbd22", "#17becf",
        "#393b79", "#637939", "#8c6d31", "#843c39", "#7b4173",
        "#3182bd", "#31a354", "#756bb1", "#636363", "#e6550d",
      ];

      const clusters = uniqueGroups.map((group, i) => {
        const groupData = data.filter((d) => groupValue(d) === group);
        const label = groupLabels[String(group)];
        const isClusterKey = groupKey === "cluster" || groupKey === "leiden";
        const displayName = label || (isClusterKey ? `Cluster ${group}` : String(group));
        const normalizedGroup = String(group).trim().toLowerCase();
        const specialColor = normalizedGroup === "annotation failed"
          ? "#dc2626"
          : normalizedGroup === "unknown"
            ? "#6b7280"
            : normalizedGroup === "unresolved"
              ? "#9ca3af"
              : undefined;
        const percent = data.length ? (100 * groupData.length) / data.length : 0;
        return {
          x: groupData.map((d) => d[xMetric]),
          y: groupData.map((d) => d[yMetric]),
          mode: "markers",
          type: "scatter",
          name: `${displayName} (${groupData.length.toLocaleString()}, ${percent.toFixed(1)}%)`,
          marker: {
            color: specialColor || plotlyColors[i % plotlyColors.length],
            size: markerSize,
            opacity: groupKey === "annotation" ? 0.6 : 0.7,
          },
          text: groupData.map(
            (d) => [
              d.barcode ? `Cell: ${d.barcode}` : null,
              d.leiden !== undefined ? `Leiden cluster: ${d.leiden}` : null,
              d.annotation ? `Annotation: ${groupKey === "annotation" ? group : d.annotation}` : `Group: ${displayName}`,
              d.annotation_confidence ? `Confidence: ${d.annotation_confidence}` : null,
              `${xMetric}: ${Number(d[xMetric] ?? 0).toFixed(2)}`,
              `${yMetric}: ${Number(d[yMetric] ?? 0).toFixed(2)}`,
            ].filter(Boolean).join("<br>"),
          ),
          hoverinfo: "text",
        };
      });
      return clusters;
    } else {
      // Single trace for expression
      const xValues = data.map((d) => d[xMetric] ?? 0);
      const yValues = data.map((d) => d[yMetric] ?? 0);
      const colorValues = data.map((d) => d[effectiveColorMetric] ?? 0);
      
      return [
        {
          type: "scatter",
          mode: "markers",
          x: xValues,
          y: yValues,
          marker: {
            color: colorValues,
            colorscale: "Viridis",
            size: markerSize,
            opacity: 0.8,
            colorbar: { title: effectiveColorMetric === "expr" ? "Gene expression" : effectiveColorMetric + " expression" },
          },
          name: `${yMetric} vs ${xMetric} colored by ${effectiveColorMetric}`,
        },
      ];
    }
  }, [data, xMetric, yMetric, colorMetric, groupKey, markerSize, groupLabels]);

  const plotComponent = (
    <Plot
      data={plotData}
      layout={{
        title: { text: title, font: { size: 14 } },
        uirevision: "survom-scatter",
        transition: { duration: 220, easing: "cubic-in-out" },
        height: undefined, // Let container control height
        autosize: true,
        paper_bgcolor: "white",
        plot_bgcolor: "white",
        margin: { l: 40, r: showLegend ? 210 : 20, t: 30, b: 30 },
        xaxis: { title: xMetric === "UMAP1" ? "UMAP 1" : xMetric, constrain: "domain" },
        yaxis: {
          title: yMetric === "UMAP2" ? "UMAP 2" : yMetric,
          scaleanchor: xMetric === "UMAP1" && yMetric === "UMAP2" ? "x" : undefined,
          scaleratio: 1,
          constrain: "domain",
        },
        ...(showLegend && {
          legend: {
            orientation: "v",
            x: 1.02,
            y: 1,
            xanchor: "left",
            font: { size: 10 },
          },
        }),
      }}
      config={{
        displayModeBar: false,
        responsive: true,
      }}
      style={{ width: "100%", height: "100%" }}
      className={plotRef ? plotRef.replace("#", "") : "plotly-plot"}
    />
  );

  return (
    <BasePlot
      title={title}
      className={className}
      showColorPicker={false}
      showSizeSlider={showSizeSlider}
      defaultSize={defaultSize}
      onSizeChange={setMarkerSize}
      downloadFilename="scatter_plot"
    >
      {plotComponent}
    </BasePlot>
  );
};

export default ScatterPlotExpression;
